





















import Vue, { PropType } from "vue";
import LoadingBlock from "@/components/loading/LoadingBlock.vue";
import MessageView, { MessageViewParam } from "@/components/MessageView.vue";
import { NavigationGuardNext, Route } from "vue-router";
import firebase from "firebase/app";
import "firebase/storage";
import { EditableSolanJournal } from "@/ts/objects/solan/editable/EditableSolanJournal";
import { Messages, messages } from "@/ts/const/Messages";
import { SolanStoreT } from "@/store/SolanStore";
import { Err } from "@/ts/objects/Err";
import { emptySaveResult, flattenSaveResults, SaveResult } from "@/ts/objects/editable/SaveResult";
import log from "loglevel";
import { PageLeaveService } from "@/ts/services/PageLeaveService";
import { SolanRepository } from "@/ts/repositories/SolanRepository";
import { UserRepository } from "@/ts/repositories/UserRepository";
import { words, Words } from "@/ts/const/Words";
import { JournalFile } from "@/ts/objects/common/JournalFile";
import { SolanJournalFile } from "@/ts/objects/solan/value/SolanJournalFile";
import SolanJournalsTable from "@/views/solan/components/SolanJournalsTable/SolanJournalsTable.vue";

const processColWidth = 100;
const ratingColWidth = 60;

export default Vue.extend({
  name: "SolanStudentViewJournalsT",
  components: {
    SolanJournalsTable,
    LoadingBlock,
    MessageView,
  },
  props: {
    solanStoreT: { type: Object as PropType<SolanStoreT>, required: true },
    userRepository: { type: Object as PropType<UserRepository>, required: true },
    solanRepository: { type: Object as PropType<SolanRepository>, required: true },
  },
  created() {
    const vm = this;
    this.periodicSaverId = window.setInterval(() => vm.saveAll(false), 15000);
    this.storage = this.$storage ?? null;
    this.pageLeaveService = new PageLeaveService({
      onLeaveStart: async () => {
        await this.saveAll(true);
      },
      requirementToLeave: async () => !this.needSave,
    });
    this.reloadData();
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      this.reloadData();
      next();
    });
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      next();
    });
  },
  beforeDestroy() {
    clearInterval(this.periodicSaverId);
  },
  data(): {
    messageView: MessageViewParam | null;

    periodicSaverId: number | undefined;
    storage: firebase.storage.Storage | null;

    journals: EditableSolanJournal[] | null;

    filesViewJournal: EditableSolanJournal | null;

    pageLeaveService: PageLeaveService | null;

    processColWidth: number;
    ratingColWidth: number;

    words: Words;
    messages: Messages;
  } {
    return {
      messageView: null,

      periodicSaverId: undefined,
      storage: null,

      journals: null,

      filesViewJournal: null,

      pageLeaveService: null,

      processColWidth,
      ratingColWidth,

      words,
      messages,
    };
  },
  computed: {
    needSave(): boolean {
      const journals = this.journals;
      if (journals === null) return false;
      return journals.some(j => j.needSave());
    },
  },
  methods: {
    async reloadData() {
      this.journals = null;
      this.messageView = null;

      const studentUserId = this.$route.params["solanStudentUserId"];
      if (studentUserId === null || studentUserId === undefined) {
        this.messageView = { message: messages.pleaseSelectStudent };
        return;
      }

      await this.solanStoreT.reloadProjectsIfStudentChanged({
        userRepository: this.userRepository,
        solanRepository: this.solanRepository,
        studentUserId,
      });

      const projectId = this.$route.params["solanProjectId"];
      if (projectId === null || projectId === undefined) {
        this.messageView = { message: messages.pleaseSelectSolanTheme };
        return;
      }

      const project = await this.solanStoreT.getProject(projectId).getDataWithTimeout();
      if (project === null) {
        this.messageView = { message: messages.pleaseSelectSolanTheme };
        return;
      }

      if (!project.started) {
        this.messageView = { message: messages.solanThemeNotStarted };
        return;
      }

      const resp = await this.solanRepository.listEditableSolanJournals(project.projectId, true, true);
      if (resp instanceof Err) {
        log.debug("Error loading project or journals!");
        this.messageView = { message: messages.failedToLoadData };
        return;
      }

      this.journals = resp.editableJournals;
    },
    async saveAll(force: boolean): Promise<SaveResult> {
      const journals = this.journals;
      if (journals === null) return emptySaveResult();
      log.debug("SAVING!");
      return flattenSaveResults(await Promise.all(journals.map(j => j.saveAllChanges(force))));
    },
    setFilesView(rubricId: string | null) {
      const journals = this.journals;
      if (rubricId === null || journals === null) {
        this.filesViewJournal = null;
        return;
      }
      const journal = journals.find(j => j.rubricId === rubricId);
      if (journal === undefined) {
        this.filesViewJournal = null;
        return;
      }
      this.filesViewJournal = journal;
    },

    async uploadJournalFile(file: File, journal: EditableSolanJournal) {
      const resp = await this.solanRepository.postJournalFile(journal.projectId, journal.rubricId, file, 30000);
      log.debug(`uploadFile: resp=${JSON.stringify(resp)}`);

      await journal.reloadJournalFiles();
    },
    async deleteJournalFile(journalFile: JournalFile, journal: EditableSolanJournal): Promise<void> {
      if (!(journalFile instanceof SolanJournalFile)) return;

      const resp = await this.solanRepository.deleteJournalFile(
        journalFile.projectId,
        journalFile.rubricId,
        journalFile.journalFileId
      );
      log.debug(`deleteFile: resp=${JSON.stringify(resp)}`);

      await journal.reloadJournalFiles();
    },
  },
});
